/**
 * Pure H OS · Workstream 03.5 · Investor Workspace
 * ==================================================
 * Master list on the left, multi-tab workspace on the right.
 * Tabs: Profile · Investments · Ownership · Distributions · Timeline · Documents · Exports
 *
 * Reads company logo / CEO from Corporate Identity — never duplicated.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  User, Users, Wallet, TrendingUp, Clock, FileText, Download,
  Plus, Save, Search, Shield, ShieldCheck, ShieldAlert, Award,
  CheckCircle2, Circle, ChevronRight,
} from "lucide-react";
import { Card, SectionHeader, Button, EmptyState } from "../../components/lib";
import {
  api, investors, Investor, Investment, OwnershipRow, Distribution,
  InvestorEvent, InvestorMeta,
} from "../../services/api";


function Pill({ tone = "neutral", children, testId }: {
  tone?: "neutral" | "success" | "warning" | "danger" | "info" | "gold";
  children: React.ReactNode; testId?: string;
}) {
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
    gold:    { bg: "var(--ph-warning-tint)", fg: "var(--ph-brand-primary)" },
  }[tone];
  return (
    <span data-testid={testId}
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded"
      style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}>
      {children}
    </span>
  );
}

function statusTone(s: string): "success" | "info" | "warning" | "danger" | "neutral" {
  return ({ active: "success", prospective: "info", exited: "neutral", on_hold: "warning" } as any)[s] || "neutral";
}
function kycTone(s: string): "success" | "warning" | "danger" | "neutral" {
  return ({ approved: "success", pending: "warning", rejected: "danger", expired: "danger", not_started: "neutral" } as any)[s] || "neutral";
}
function amlTone(s: string): "success" | "warning" | "danger" | "neutral" {
  return ({ clear: "success", pending: "warning", flagged: "danger", not_screened: "neutral" } as any)[s] || "neutral";
}
function fmtMoney(n?: number | null, ccy = "EGP") {
  if (n === null || n === undefined) return "—";
  return `${ccy} ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function fmtPct(n?: number | null) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(2)}%`;
}

function InvestorAvatar({ investor, size = 40 }: { investor: Partial<Investor>; size?: number }) {
  const initials = (investor.fullName || "?").split(" ").slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  if (investor.photoDataUrl) {
    return <img src={investor.photoDataUrl} alt={investor.fullName || "investor"} className="rounded-full object-cover shrink-0"
                 style={{ width: size, height: size, border: "1px solid var(--ph-border-subtle)" }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center font-bold shrink-0"
         style={{
           width: size, height: size,
           background: "var(--ph-surface-alt)",
           color: "var(--ph-brand-primary)",
           border: "1px solid var(--ph-border-subtle)",
           fontSize: size * 0.36,
         }}>
      {initials || "?"}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────
export default function InvestorWorkspace() {
  const [list, setList] = useState<Investor[]>([]);
  const [meta, setMeta] = useState<InvestorMeta | null>(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Investor | null>(null);
  const [tab, setTab] = useState<"profile" | "investments" | "ownership" | "distributions" | "timeline" | "documents" | "exports">("profile");
  const [flash, setFlash] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const rows = await investors.list({ q: q || undefined, status: statusFilter || undefined });
      setList(rows);
    } catch (e: any) {
      setFlash(e?.message || "Failed to load");
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { investors.meta().then(setMeta).catch(() => setMeta(null)); }, []);

  useEffect(() => {
    const id = searchParams.get("investorId");
    if (id) setSelectedId(id);
  }, [searchParams]);

  const reloadSelected = useCallback(async () => {
    if (!selectedId) { setSelected(null); return; }
    try {
      const d = await investors.get(selectedId);
      setSelected(d);
    } catch { setSelected(null); }
  }, [selectedId]);

  useEffect(() => { reloadSelected(); }, [reloadSelected]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  const total = selected?.rollup && "total" in (selected.rollup as any)
    ? (selected.rollup as any).total
    : (selected?.rollup as any);
  const byVenture: any[] = selected?.rollup && "byVenture" in (selected.rollup as any)
    ? (selected.rollup as any).byVenture
    : [];

  return (
    <div className="p-6 space-y-4" data-testid="investor-workspace">
      <SectionHeader
        eyebrow="Workstream 03.5 · Capital"
        title="Investor Workspace"
        subtitle="Holding-level master list · Every investment and ownership entry is venture-scoped"
      />

      <div className="grid grid-cols-12 gap-4">
        {/* Left · list */}
        <div className="col-span-4">
          <Card padded={false} testId="investor-list">
            <div className="p-3 border-b space-y-2" style={{ borderColor: "var(--ph-border-subtle)" }}>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={13} className="absolute left-2 top-2 text-slate-400" />
                  <input className="w-full pl-7 pr-3 py-1.5 text-sm border rounded"
                          style={{ borderColor: "var(--ph-border-strong)" }}
                          placeholder="Search name / code / email…"
                          value={q} onChange={(e) => setQ(e.target.value)}
                          data-testid="investor-search" />
                </div>
                <Button variant="primary" onClick={() => setShowCreate(true)} testId="investor-new-btn">
                  <Plus size={13} />
                </Button>
              </div>
              <select className="w-full text-xs px-2 py-1 border rounded"
                       style={{ borderColor: "var(--ph-border-strong)" }}
                       value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                       data-testid="investor-filter-status">
                <option value="">All statuses</option>
                {meta?.status.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {!list.length && (
                <div className="p-6 text-xs text-slate-500 text-center">
                  No investors yet. Click <b>+</b> to add your first.
                </div>
              )}
              {list.map((iv) => (
                <button key={iv.id} onClick={() => setSelectedId(iv.id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 border-b hover:opacity-80 text-left"
                        style={{
                          borderColor: "var(--ph-border-subtle)",
                          background: iv.id === selectedId ? "var(--ph-surface-alt)" : "transparent",
                        }}
                        data-testid={`investor-row-${iv.investorCode}`}>
                  <InvestorAvatar investor={iv} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{iv.fullName}</div>
                    <div className="text-[11px] text-slate-500">{iv.investorCode} · {iv.ventureCount || 0} venture(s)</div>
                  </div>
                  <div className="text-right shrink-0">
                    <Pill tone={statusTone(iv.status)}>{iv.status.replace("_", " ")}</Pill>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right · detail */}
        <div className="col-span-8">
          {!selected && (
            <Card testId="investor-empty">
              <EmptyState icon={Users} title="Select an investor"
                            message="Pick an investor on the left or click + to create a new profile." />
            </Card>
          )}

          {selected && (
            <>
              <InvestorHeaderCard investor={selected} total={total} />

              {/* Tabs */}
              <div className="flex gap-1 border-b overflow-x-auto mb-4 mt-4"
                   style={{ borderColor: "var(--ph-border-subtle)" }}>
                {[
                  { key: "profile",       label: "Profile",       Icon: User },
                  { key: "investments",   label: "Investments",   Icon: Wallet },
                  { key: "ownership",     label: "Ownership",     Icon: TrendingUp },
                  { key: "distributions", label: "Distributions", Icon: TrendingUp },
                  { key: "timeline",      label: "Timeline",      Icon: Clock },
                  { key: "documents",     label: "Documents",     Icon: FileText },
                  { key: "exports",       label: "Exports",       Icon: Download },
                ].map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key as any)}
                          data-testid={`iw-tab-${t.key}`}
                          className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition"
                          style={{
                            borderColor: tab === t.key ? "var(--ph-brand-primary)" : "transparent",
                            color: tab === t.key ? "var(--ph-brand-primary)" : "var(--ph-ink)",
                          }}>
                    <t.Icon size={12} /> {t.label}
                  </button>
                ))}
              </div>

              {tab === "profile"       && <ProfileTab       investor={selected} meta={meta} onReload={reloadSelected} onFlash={setFlash} />}
              {tab === "investments"   && <InvestmentsTab   investor={selected} meta={meta} onReload={reloadSelected} onFlash={setFlash} />}
              {tab === "ownership"     && <OwnershipTab     investor={selected} onReload={reloadSelected} onFlash={setFlash} />}
              {tab === "distributions" && <DistributionsTab investor={selected} meta={meta} onReload={reloadSelected} onFlash={setFlash} />}
              {tab === "timeline"      && <TimelineTab      investor={selected} />}
              {tab === "documents"     && <DocumentsTab     investor={selected} />}
              {tab === "exports"       && <ExportsTab       investor={selected} byVenture={byVenture} onFlash={setFlash} />}
            </>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateInvestorModal
          meta={meta}
          onClose={() => setShowCreate(false)}
          onCreated={(iv) => { setShowCreate(false); setSelectedId(iv.id); load(); setFlash(`Created ${iv.investorCode}`); }}
        />
      )}

      {flash && (
        <div className="fixed bottom-6 right-6 px-4 py-2 rounded shadow-lg z-50"
             style={{ background: "var(--ph-brand-primary, #1a1a1a)", color: "#F7F3EB", fontSize: 12 }}
             data-testid="iw-flash">{flash}</div>
      )}
    </div>
  );
}

// ── Header card ──────────────────────────────────────────────────
function InvestorHeaderCard({ investor, total }: { investor: Investor; total: any }) {
  return (
    <Card testId="iw-header">
      <div className="flex items-start gap-4">
        <InvestorAvatar investor={investor} size={64} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xl font-bold" style={{ letterSpacing: "0.03em" }}>{investor.fullName}</div>
            <Pill tone={statusTone(investor.status)} testId="iw-status-pill">{investor.status.replace("_", " ")}</Pill>
            <Pill tone={kycTone(investor.kycStatus)}>KYC · {investor.kycStatus.replace("_", " ")}</Pill>
            <Pill tone={amlTone(investor.amlStatus)}>AML · {investor.amlStatus.replace("_", " ")}</Pill>
          </div>
          <div className="text-xs text-slate-500 mt-1">{investor.investorCode} · joined {investor.joinDate || "—"}</div>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-3 mt-3 pt-3 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <Tile label="Committed"       value={fmtMoney(total?.capitalCommitted)}     testId="iw-tile-committed" />
        <Tile label="Paid"            value={fmtMoney(total?.capitalPaid)}          testId="iw-tile-paid" />
        <Tile label="Remaining"       value={fmtMoney(total?.capitalRemaining)}     testId="iw-tile-remaining" />
        <Tile label="Distributions"   value={fmtMoney(total?.distributionsPaid)}    testId="iw-tile-dist" />
        <Tile label="Ventures"        value={String(total?.activeVentureCount || 0)} testId="iw-tile-ventures" />
      </div>
    </Card>
  );
}
function Tile({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
      <div className="text-base font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

// ── Profile tab ─────────────────────────────────────────────────
function ProfileTab({ investor, meta, onReload, onFlash }: {
  investor: Investor; meta: InvestorMeta | null;
  onReload: () => Promise<void>; onFlash: (m: string) => void;
}) {
  const [form, setForm] = useState<any>(investor);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(investor), [investor.id]);

  const save = async () => {
    setSaving(true);
    try {
      await investors.patch(investor.id, form);
      await onReload();
      onFlash("Profile saved");
    } catch (e: any) { onFlash(e?.message || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Card testId="iw-profile-tab">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full name">
          <Input value={form.fullName || ""} onChange={(v) => setForm({ ...form, fullName: v })} testId="iw-full-name" />
        </Field>
        <Field label="Status">
          <Select value={form.status || "active"} onChange={(v) => setForm({ ...form, status: v })} options={meta?.status || []} testId="iw-status" />
        </Field>
        <Field label="Investor class">
          <Select
            value={form.investorClass || meta?.investorClassDefault || "Founding Investor"}
            onChange={(v) => setForm({ ...form, investorClass: v })}
            options={meta?.investorClass || []}
            testId="iw-investor-class"
          />
        </Field>
        <Field label="Email"><Input value={form.email || ""} onChange={(v) => setForm({ ...form, email: v })} /></Field>
        <Field label="Phone"><Input value={form.phone || ""} onChange={(v) => setForm({ ...form, phone: v })} /></Field>
        <Field label="Join date"><Input type="date" value={(form.joinDate || "").slice(0, 10)} onChange={(v) => setForm({ ...form, joinDate: v })} /></Field>
        <Field label="Photo (data URL, optional)">
          <textarea value={form.photoDataUrl || ""} onChange={(e) => setForm({ ...form, photoDataUrl: e.target.value })}
                     rows={2}
                     className="w-full px-3 py-2 text-xs border rounded font-mono"
                     style={{ borderColor: "var(--ph-border-strong)" }}
                     placeholder="data:image/jpeg;base64,…" />
        </Field>
      </div>

      <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Compliance</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="KYC status">
            <Select value={form.kycStatus || "not_started"} onChange={(v) => setForm({ ...form, kycStatus: v })}
                     options={meta?.kycStatus || []} testId="iw-kyc-status" />
          </Field>
          <Field label="AML status">
            <Select value={form.amlStatus || "not_screened"} onChange={(v) => setForm({ ...form, amlStatus: v })}
                     options={meta?.amlStatus || []} testId="iw-aml-status" />
          </Field>
          <Field label="KYC notes"><textarea value={form.kycNotes || ""} onChange={(e) => setForm({ ...form, kycNotes: e.target.value })}
                                                rows={2} className="w-full px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} /></Field>
          <Field label="AML notes"><textarea value={form.amlNotes || ""} onChange={(e) => setForm({ ...form, amlNotes: e.target.value })}
                                                rows={2} className="w-full px-3 py-2 text-sm border rounded" style={{ borderColor: "var(--ph-border-strong)" }} /></Field>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Banking</div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Bank"><Input value={form.bank?.bankName || ""} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), bankName: v } })} /></Field>
          <Field label="Account name"><Input value={form.bank?.accountName || ""} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), accountName: v } })} /></Field>
          <Field label="IBAN"><Input value={form.bank?.iban || ""} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), iban: v } })} /></Field>
          <Field label="Account #"><Input value={form.bank?.accountNumber || ""} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), accountNumber: v } })} /></Field>
          <Field label="SWIFT"><Input value={form.bank?.swift || ""} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), swift: v } })} /></Field>
          <Field label="Currency"><Input value={form.bank?.currency || "EGP"} onChange={(v) => setForm({ ...form, bank: { ...(form.bank || {}), currency: v } })} /></Field>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="primary" onClick={save} disabled={saving} testId="iw-save-profile">
          <Save size={14} /> {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </Card>
  );
}

// ── Investments tab ─────────────────────────────────────────────
function InvestmentsTab({ investor, meta, onReload, onFlash }: {
  investor: Investor; meta: InvestorMeta | null;
  onReload: () => Promise<void>; onFlash: (m: string) => void;
}) {
  const [rows, setRows] = useState<Investment[]>([]);
  const [ventures, setVentures] = useState<Array<{ id: string; name: string }>>([]);
  const [ventureId, setVentureId] = useState("orvan");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ccy, setCcy] = useState("EGP");
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");

  const load = useCallback(async () => {
    const r = await investors.investments(investor.id);
    setRows(r);
  }, [investor.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.list("ventures").then((vs: any) => {
      const list = ((vs as any[]) || []).map((v) => ({ id: v.id, name: v.name || v.id }));
      if (list.length) setVentures(list);
    }).catch(() => setVentures([{ id: "orvan", name: "ORVAN Perfumes" }]));
  }, []);

  const add = async () => {
    if (!amount || Number(amount) <= 0) { onFlash("Amount must be positive"); return; }
    try {
      await investors.createInvestment(investor.id, {
        ventureId, date, amount: Number(amount), currency: ccy,
        paymentMethod: method, reference: reference || undefined,
      } as any);
      setAmount(""); setReference("");
      await load(); await onReload();
      onFlash("Investment recorded");
    } catch (e: any) { onFlash(e?.message || "Add failed"); }
  };

  const approve = async (id: string) => {
    try {
      await investors.approveInvestment(id);
      await load(); await onReload();
      onFlash("Approved");
    } catch (e: any) { onFlash(e?.message || "Approve failed"); }
  };

  return (
    <Card testId="iw-investments-tab">
      <div className="grid grid-cols-6 gap-2 mb-3 pb-3 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <Select value={ventureId} onChange={setVentureId} options={ventures.map((v) => v.id)} testId="iw-inv-venture" />
        <Input type="date" value={date} onChange={setDate} testId="iw-inv-date" />
        <Input type="number" value={amount} onChange={setAmount} placeholder="Amount" testId="iw-inv-amount" />
        <Select value={ccy} onChange={setCcy} options={meta?.currencies || ["EGP"]} testId="iw-inv-ccy" />
        <Select value={method} onChange={setMethod} options={meta?.paymentMethods || []} testId="iw-inv-method" />
        <Input value={reference} onChange={setReference} placeholder="Reference / txn #" testId="iw-inv-ref" />
      </div>
      <Button variant="primary" onClick={add} testId="iw-inv-add">
        <Plus size={13} /> Add investment
      </Button>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--ph-surface-alt)" }}>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">Venture</th>
              <th className="px-3 py-2 font-semibold text-right">Amount</th>
              <th className="px-3 py-2 font-semibold">Method</th>
              <th className="px-3 py-2 font-semibold">Reference</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={7} className="p-6 text-center text-xs text-slate-500">No investments yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid={`iw-inv-row-${r.id}`}>
                <td className="px-3 py-2 text-xs">{r.date}</td>
                <td className="px-3 py-2 text-xs">{r.ventureId}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmtMoney(r.amount, r.currency)}</td>
                <td className="px-3 py-2 text-xs">{r.paymentMethod.replace("_", " ")}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.reference || "—"}</td>
                <td className="px-3 py-2"><Pill tone={r.status === "approved" ? "success" : "warning"}>{r.status.replace("_", " ")}</Pill></td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "approved" && (
                    <button onClick={() => approve(r.id)} className="text-xs font-semibold underline decoration-dotted"
                            data-testid={`iw-inv-approve-${r.id}`}>Approve</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Ownership tab ───────────────────────────────────────────────
function OwnershipTab({ investor, onReload, onFlash }: {
  investor: Investor; onReload: () => Promise<void>; onFlash: (m: string) => void;
}) {
  const [rows, setRows] = useState<OwnershipRow[]>([]);
  const [ventureId, setVentureId] = useState("orvan");
  const [pct, setPct] = useState("");
  const [committed, setCommitted] = useState("");
  const [reason, setReason] = useState("subscription");

  const load = useCallback(async () => {
    setRows(await investors.ownership(investor.id));
  }, [investor.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!pct || Number(pct) < 0) { onFlash("Enter a valid ownership %"); return; }
    try {
      const r = await investors.setOwnership(investor.id, {
        ventureId, ownershipPct: Number(pct),
        capitalCommitted: Number(committed || 0),
        effectiveAt: new Date().toISOString(), reason,
      } as any);
      setPct(""); setCommitted("");
      await load(); await onReload();
      onFlash(r._warning || "Ownership updated");
    } catch (e: any) { onFlash(e?.message || "Save failed"); }
  };

  return (
    <Card testId="iw-ownership-tab">
      <div className="grid grid-cols-5 gap-2 mb-3 pb-3 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <Input value={ventureId} onChange={setVentureId} placeholder="Venture ID" testId="iw-own-venture" />
        <Input type="number" value={pct} onChange={setPct} placeholder="Ownership %" testId="iw-own-pct" />
        <Input type="number" value={committed} onChange={setCommitted} placeholder="Capital committed" testId="iw-own-committed" />
        <Select value={reason} onChange={setReason}
                 options={["subscription", "dilution", "transfer", "adjustment", "exit"]} testId="iw-own-reason" />
        <Button variant="primary" onClick={save} testId="iw-own-add"><Plus size={13} /> Add / update</Button>
      </div>

      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Ownership history</div>
      {!rows.length && <div className="p-4 text-xs text-slate-500 text-center">No ownership records yet.</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--ph-surface-alt)" }}>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                <th className="px-3 py-2 font-semibold">Effective</th>
                <th className="px-3 py-2 font-semibold">Venture</th>
                <th className="px-3 py-2 font-semibold text-right">Ownership %</th>
                <th className="px-3 py-2 font-semibold text-right">Committed</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                  <td className="px-3 py-2 text-xs">{(r.effectiveAt || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">{r.ventureId}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtPct(r.ownershipPct)}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(r.capitalCommitted)}</td>
                  <td className="px-3 py-2 text-xs">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Distributions tab ───────────────────────────────────────────
function DistributionsTab({ investor, meta, onReload, onFlash }: {
  investor: Investor; meta: InvestorMeta | null;
  onReload: () => Promise<void>; onFlash: (m: string) => void;
}) {
  const [rows, setRows] = useState<Distribution[]>([]);
  const [ventureId, setVentureId] = useState("orvan");
  const [period, setPeriod] = useState("2026-Q1");
  const [amount, setAmount] = useState("");
  const [ccy, setCcy] = useState("EGP");
  const [status, setStatus] = useState<"pending" | "paid">("pending");

  const load = useCallback(async () => {
    setRows(await investors.distributions(investor.id));
  }, [investor.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!amount || Number(amount) <= 0) { onFlash("Amount required"); return; }
    try {
      await investors.createDistribution(investor.id, {
        ventureId, period, amount: Number(amount), currency: ccy, status,
        paidAt: status === "paid" ? new Date().toISOString().slice(0, 10) : undefined,
      } as any);
      setAmount("");
      await load(); await onReload();
      onFlash("Distribution recorded");
    } catch (e: any) { onFlash(e?.message || "Add failed"); }
  };

  const paid = rows.filter((r) => r.status === "paid");
  const pending = rows.filter((r) => r.status !== "paid");
  const total = (r: Distribution[]) => r.reduce((s, x) => s + Number(x.amount || 0), 0);

  return (
    <Card testId="iw-distributions-tab">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded" style={{ background: "var(--ph-success-tint)" }}>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--ph-success)" }}>Paid</div>
          <div className="text-xl font-bold mt-0.5">{fmtMoney(total(paid))}</div>
          <div className="text-xs text-slate-600 mt-0.5">{paid.length} record(s)</div>
        </div>
        <div className="p-3 rounded" style={{ background: "var(--ph-warning-tint)" }}>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--ph-warning)" }}>Pending</div>
          <div className="text-xl font-bold mt-0.5">{fmtMoney(total(pending))}</div>
          <div className="text-xs text-slate-600 mt-0.5">{pending.length} record(s)</div>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-2 mb-3 pb-3 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <Input value={ventureId} onChange={setVentureId} placeholder="Venture" />
        <Input value={period} onChange={setPeriod} placeholder="2026-Q1" />
        <Input type="number" value={amount} onChange={setAmount} placeholder="Amount" testId="iw-dist-amount" />
        <Select value={ccy} onChange={setCcy} options={meta?.currencies || ["EGP"]} />
        <Select value={status} onChange={(v) => setStatus(v as any)} options={["pending", "paid"]} testId="iw-dist-status" />
        <Button variant="primary" onClick={add} testId="iw-dist-add"><Plus size={13} /> Add</Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--ph-surface-alt)" }}>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
              <th className="px-3 py-2 font-semibold">Period</th>
              <th className="px-3 py-2 font-semibold">Venture</th>
              <th className="px-3 py-2 font-semibold text-right">Amount</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Paid at</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={5} className="p-6 text-center text-xs text-slate-500">No distributions yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid={`iw-dist-row-${r.id}`}>
                <td className="px-3 py-2 font-semibold">{r.period}</td>
                <td className="px-3 py-2 text-xs">{r.ventureId}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(r.amount, r.currency)}</td>
                <td className="px-3 py-2"><Pill tone={r.status === "paid" ? "success" : "warning"}>{r.status}</Pill></td>
                <td className="px-3 py-2 text-xs">{r.paidAt || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Timeline tab ────────────────────────────────────────────────
function TimelineTab({ investor }: { investor: Investor }) {
  const [rows, setRows] = useState<InvestorEvent[]>([]);
  useEffect(() => { investors.timeline(investor.id).then(setRows).catch(() => setRows([])); }, [investor.id]);
  return (
    <Card testId="iw-timeline-tab">
      {!rows.length && <div className="text-xs text-slate-500 text-center p-6">No activity yet.</div>}
      <div className="space-y-2">
        {rows.map((e) => (
          <div key={e.id} className="flex gap-3 py-2 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
            <div className="text-xs font-mono text-slate-500 shrink-0" style={{ width: 90 }}>{(e.at || "").slice(0, 10)}</div>
            <div className="flex-1">
              <div className="text-sm font-semibold">{e.title}</div>
              {e.body && <div className="text-xs text-slate-500">{e.body}</div>}
              <div className="text-[10px] text-slate-400 mt-0.5">{e.kind}{e.ventureId ? ` · ${e.ventureId}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Documents tab ───────────────────────────────────────────────
function DocumentsTab({ investor }: { investor: Investor }) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => { investors.documents(investor.id).then(setRows).catch(() => setRows([])); }, [investor.id]);
  const byCategory = useMemo(() => {
    const m: Record<string, any[]> = {};
    rows.forEach((r) => { (m[r.category] = m[r.category] || []).push(r); });
    return m;
  }, [rows]);
  return (
    <Card testId="iw-docs-tab">
      {!rows.length && <div className="text-xs text-slate-500 text-center p-6">No documents attached yet.</div>}
      {Object.entries(byCategory).map(([cat, list]) => (
        <div key={cat} className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">{cat}</div>
          {list.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-1.5 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
              <FileText size={14} className="opacity-50" />
              <div className="text-sm flex-1">{d.name}</div>
              <div className="text-xs text-slate-500">{Math.round((d.sizeBytes || 0) / 1024)} KB</div>
            </div>
          ))}
        </div>
      ))}
    </Card>
  );
}

// ── Exports tab ─────────────────────────────────────────────────
function ExportsTab({ investor, byVenture, onFlash }: {
  investor: Investor; byVenture: any[]; onFlash: (m: string) => void;
}) {
  const download = async (kind: "profile" | "statement" | "certificate", ventureId?: string) => {
    try {
      const url = investors.exportUrl(investor.id, kind, ventureId);
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${investor.investorCode}-${kind}${ventureId ? "-" + ventureId : ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
      onFlash(`${kind[0].toUpperCase() + kind.slice(1)} downloaded`);
    } catch (e: any) { onFlash(e?.message || "Export failed"); }
  };

  return (
    <Card testId="iw-exports-tab">
      <div className="grid grid-cols-2 gap-4">
        <ExportCard title="Investor Profile" description="Complete biographical dossier including KYC/AML, banking, ventures and activity."
                     Icon={User} onClick={() => download("profile")} testId="iw-export-profile" />
        <ExportCard title="Investment Statement" description="Chronological investment ledger with running totals and profit distributions."
                     Icon={Wallet} onClick={() => download("statement")} testId="iw-export-statement" />
      </div>
      <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Investment Certificate</div>
        <div className="text-xs text-slate-600 mb-3">
          A luxury executive certificate. Pick the venture — logo, stamp and CEO signature are
          automatically read from Corporate Identity for that venture.
        </div>
        {!byVenture?.length && <div className="text-xs text-slate-500">Record an ownership entry to enable certificates.</div>}
        <div className="grid grid-cols-2 gap-3">
          {byVenture.map((v) => (
            <div key={v.ventureId} className="p-3 rounded flex items-center justify-between"
                  style={{ background: "var(--ph-surface-alt)", border: "1px solid var(--ph-border-subtle)" }}>
              <div>
                <div className="text-sm font-semibold">{v.ventureId}</div>
                <div className="text-xs text-slate-600">
                  {fmtPct(v.ownershipPct)} · {fmtMoney(v.capitalPaid, v.currency)}
                </div>
              </div>
              <Button variant="primary" onClick={() => download("certificate", v.ventureId)}
                       testId={`iw-export-cert-${v.ventureId}`}>
                <Award size={13} /> Generate
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function ExportCard({ title, description, Icon, onClick, testId }: {
  title: string; description: string; Icon: any;
  onClick: () => void; testId?: string;
}) {
  return (
    <div className="p-4 rounded border flex flex-col gap-3"
          style={{ borderColor: "var(--ph-border-subtle)", background: "var(--ph-surface)" }}>
      <div className="flex items-center gap-2">
        <Icon size={18} className="opacity-70" />
        <div className="text-sm font-bold">{title}</div>
      </div>
      <div className="text-xs text-slate-600 flex-1">{description}</div>
      <Button variant="primary" onClick={onClick} testId={testId}>
        <Download size={12} /> Download PDF
      </Button>
    </div>
  );
}

// ── Create modal ────────────────────────────────────────────────
function CreateInvestorModal({ meta, onClose, onCreated }: {
  meta: InvestorMeta | null; onClose: () => void; onCreated: (iv: Investor) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const created = await investors.create({ fullName, email: email || undefined, phone: phone || undefined });
      onCreated(created);
    } catch (e: any) { setErr(e?.message || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="iw-create-modal">
        <div className="p-4 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Investor</div>
          <div className="text-lg font-bold">New investor profile</div>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="text-xs px-3 py-2 rounded" style={{ background: "var(--ph-danger-tint)", color: "var(--ph-danger)" }}>{err}</div>}
          <Field label="Full name (required)">
            <Input value={fullName} onChange={setFullName} testId="iw-create-name" />
          </Field>
          <Field label="Email"><Input value={email} onChange={setEmail} /></Field>
          <Field label="Phone"><Input value={phone} onChange={setPhone} /></Field>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !fullName.trim()} testId="iw-create-submit">
            <Save size={13} /> {busy ? "Creating…" : "Create investor"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Small form primitives ───────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}
function Input({ value, onChange, type = "text", placeholder, testId }: {
  value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; testId?: string;
}) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} type={type} placeholder={placeholder}
           data-testid={testId}
           className="w-full px-3 py-2 text-sm border rounded"
           style={{ borderColor: "var(--ph-border-strong)" }} />
  );
}
function Select({ value, onChange, options, testId }: {
  value: string; onChange: (v: string) => void; options: string[]; testId?: string;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
             className="w-full px-3 py-2 text-sm border rounded"
             style={{ borderColor: "var(--ph-border-strong)" }}>
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  );
}
